/**
 * MenuFontPicker — sélecteur de polices avec aperçu WYSIWYG.
 *
 * Chaque nom de police est affiché DANS la police qu'il représente
 * (contrairement au `<select>` natif, où Chrome/Safari ignorent
 * `font-family` sur `<option>`). Popover accessible clavier + tactile.
 * Charge dynamiquement les polices depuis Google Fonts la première fois.
 */
import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

// Charge les polices Google Fonts une seule fois par session. Le PublicMenu
// possède déjà `loadMenuFonts` — on duplique ici pour l'éditeur.
function ensureFonts(families) {
  const uniq = Array.from(new Set(families.filter(Boolean)));
  if (!uniq.length) return;
  const id = "editor-fonts-" + uniq.join("|").replace(/\s+/g, "+");
  if (document.getElementById(id)) return;
  const fams = uniq.map((f) => `family=${encodeURIComponent(f)}:wght@400;600;700`).join("&");
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${fams}&display=swap`;
  document.head.appendChild(link);
}

export default function MenuFontPicker({ value, onChange, options, testId, label }) {
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const btnRef = useRef();
  const listRef = useRef();

  useEffect(() => { ensureFonts(options); }, [options]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!btnRef.current) return;
      if (btnRef.current.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const pick = (f) => { onChange(f); setOpen(false); btnRef.current?.focus(); };
  const onKey = (e) => {
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault(); setOpen(true); setFocusIdx(Math.max(0, options.indexOf(value))); return;
    }
    if (!open) return;
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusIdx((i) => (i + 1) % options.length); return; }
    if (e.key === "ArrowUp")   { e.preventDefault(); setFocusIdx((i) => (i - 1 + options.length) % options.length); return; }
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(options[focusIdx] || value); return; }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onKey}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label ? `Choisir la police pour ${label}` : "Choisir la police"}
        className="w-full flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[13px] hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF9F1C]"
        style={{ fontFamily: `"${value}", system-ui, sans-serif` }}
      >
        <span className="truncate">{value}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      </button>
      {open && (
        <div
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          data-testid={`${testId}-listbox`}
          className="absolute z-30 mt-1 w-full max-h-[280px] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-[0_12px_28px_rgba(11,31,51,0.16)] py-1"
        >
          {options.map((f, i) => {
            const selected = f === value;
            const focused = i === focusIdx;
            return (
              <button
                key={f}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => pick(f)}
                onMouseEnter={() => setFocusIdx(i)}
                data-testid={`${testId}-opt-${f.replace(/\s+/g, "_")}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[14px] transition-colors ${
                  focused ? "bg-[#FFF7E9]" : selected ? "bg-slate-50" : "bg-white hover:bg-slate-50"
                }`}
                style={{ fontFamily: `"${f}", system-ui, sans-serif` }}
              >
                <span className="truncate">{f}</span>
                {selected && <Check className="w-3.5 h-3.5 text-[#FF9F1C] shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
